import React, { useContext, useEffect } from "react";
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  Area, AreaChart, PieChart, Pie, Legend, Cell, BarChart, Bar,
} from "recharts";
import ContentPage from "../Content";
import moment from "moment";
import separateNumber from "../../utils/separateur";
import ExportExcel from "../../utils/ExcelExport";
import { UserContext } from "../../utils/User_check";
import can from "../../utils/Can";
import { useNavigate } from "react-router-dom";
import useMomoDashboard from "../../hooks/useMomoDashboard";
import PageHeader from "../ui/PageHeader";
import StatCard from "../ui/StatCard";
import SectionCard from "../ui/SectionCard";
import EmptyState from "../ui/EmptyState";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

function MomoHomePage() {
  const { user, permissions } = useContext(UserContext);
  const navigate = useNavigate();

  const {
    dateDebut, setDateDebut,
    dateFin, setDateFin,
    dateCapture, setDateCapture,
    loadingStats, loadingImport,
    TotalApproDay, TotalCashin, TotalCashout, NbreCashin, NbreCashout,
    dataSecteurCashin, dataSecteurCashout,
    recapCommercials, topPos, venteSerie,
    loadDashboard, resetState,
    handleFile, submitImport,
  } = useMomoDashboard();

  const datapie = [
    { name: "CASH IN (%)", value: TotalApproDay ? TotalCashin * 100 / TotalApproDay : 0 },
    { name: "CASH OUT (%)", value: TotalApproDay ? TotalCashout * 100 / TotalApproDay : 0 },
  ];

  const sortedData = [...recapCommercials].sort((a, b) => b.MomoTotal - a.MomoTotal);

  useEffect(() => {
    if (user && permissions && !can(permissions, "TABLEAU DE BORD MOMO")) {
      navigate("/page-no-found");
    }
  }, [user]);

  useEffect(() => {
    loadDashboard(null, null);
  }, []);

  const periodeLabel = dateFin == null
    ? moment().format("Do MMMM")
    : `${moment(dateDebut).format("Do MMMM")} au ${moment(dateFin).format("Do MMMM")}`;

  const filtre_periode_data = async (e) => {
    e.preventDefault();
    resetState();
    await loadDashboard(dateDebut, dateFin);
  };

  const today_data_fetch = () => {
    setDateDebut(null);
    setDateFin(null);
    window.location.reload();
  };

  return (
    <>
      <ContentPage>
        <PageHeader eyebrow="Rapport Momo" title="Tableau de bord Momo" periodLabel={periodeLabel}>
          <button
            className="btn btn-brand btn-icon-text"
            onClick={() => window.$("#exampleModalCenteContrat").modal("show")}
          >
            <i className="mdi mdi-upload"></i> Importer le fichier Momo
          </button>
          <button type="button" className="btn btn-outline-primary btn-icon-text" onClick={today_data_fetch}>
            <i className="mdi mdi-refresh"></i> Actualiser
          </button>
          <button
            type="button"
            className="btn btn-outline-brand btn-icon-text"
            onClick={() => window.$("#exampleModalFiltre").modal("show")}
          >
            <i className="mdi mdi-filter-variant"></i> Filtrer
          </button>
        </PageHeader>

        {/* Volume CICO + Cash In + Cash Out */}
        <div className="row">
          <div className="col-sm-4 stretch-card grid-margin">
            <StatCard
              icon="mdi-bank-transfer"
              color="gold"
              label="Volume CICO"
              sublabel={periodeLabel}
              value={separateNumber(TotalApproDay)}
              suffix="FCFA"
              footer="Cash In / Cash Out"
              loading={loadingStats}
            />
          </div>
          <div className="col-sm-4 stretch-card grid-margin">
            <StatCard
              icon="mdi-cash-plus"
              color="danger"
              label="Total cash in"
              sublabel={periodeLabel}
              value={separateNumber(TotalCashin)}
              suffix="FCFA"
              footer="Somme des cash in"
              loading={loadingStats}
            />
          </div>
          <div className="col-sm-4 stretch-card grid-margin">
            <StatCard
              icon="mdi-cash-minus"
              color="danger"
              label="Total cash out"
              sublabel={periodeLabel}
              value={separateNumber(TotalCashout)}
              suffix="FCFA"
              footer="Somme des cash out"
              loading={loadingStats}
            />
          </div>
        </div>

        {/* Nombre de transactions */}
        <div className="row">
          <div className="col-sm-6 stretch-card grid-margin">
            <StatCard
              icon="mdi-swap-horizontal"
              color="danger"
              label="Nombre de transaction cash in"
              sublabel={periodeLabel}
              value={separateNumber(NbreCashin)}
              loading={loadingStats}
            />
          </div>
          <div className="col-sm-6 stretch-card grid-margin">
            <StatCard
              icon="mdi-swap-horizontal-bold"
              color="success"
              label="Nombre de transaction cash out"
              sublabel={periodeLabel}
              value={separateNumber(NbreCashout)}
              loading={loadingStats}
            />
          </div>
        </div>

        {/* Pie Chart CICO */}
        <div className="row">
          <div className="col-xl-12 stretch-card grid-margin">
            <SectionCard icon="mdi-chart-donut" color="gold" title={`Statistique des ventes CICO du ${periodeLabel}`} bodyClassName="p-0 d-flex justify-content-center">
              {!loadingStats ? (
                <PieChart width={720} height={380}>
                  <Pie data={datapie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={120} fill="#8884d8" label>
                    {COLORS.map((color, index) => <Cell key={`cell-${index}`} fill={color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              ) : (
                <div className="d-flex justify-content-center align-items-center" style={{minHeight:300}}>
                  <div className="spinner-border text-primary" role="status"></div>
                </div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Secteur Cash In */}
        <div className="row">
          <div className="col-sm-12 stretch-card grid-margin">
            <SectionCard icon="mdi-chart-bar" color="gold" title={`Statistique des ventes cash in par secteurs du ${periodeLabel}`}>
              {!loadingStats ? (
                dataSecteurCashin?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataSecteurCashin} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#FF8042" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState icon="mdi-chart-bar" title="Aucune donnée sur cette période" />
                )
              ) : (
                <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Secteur Cash Out */}
        <div className="row">
          <div className="col-sm-12 stretch-card grid-margin">
            <SectionCard icon="mdi-chart-bar" color="success" title={`Statistique des ventes cash out par secteurs du ${periodeLabel}`}>
              {!loadingStats ? (
                dataSecteurCashout?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dataSecteurCashout} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#00C49F" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <EmptyState icon="mdi-chart-bar" title="Aucune donnée sur cette période" />
                )
              ) : (
                <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Top 10 POS MOMO */}
        <div className="row">
          <div className="col-xl-12 stretch-card grid-margin">
            <SectionCard
              icon="mdi-trophy-award"
              color="gold"
              title={`Top 10 des vendeurs CICO ${periodeLabel}`}
              actions={<ExportExcel excelData={topPos} filename="TOP VENDEURS MOMO/POS" title="Télécharger" color="btn-success" />}
              bodyClassName="p-0"
            >
              {topPos?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-striped align-middle mb-0">
                    <thead>
                      <tr>
                        <th>NOM DU PDV</th>
                        <th>NUMERO</th>
                        <th>NBRE CASH IN</th>
                        <th>NBRE CASH OUT</th>
                        <th>VENTE CASH IN</th>
                        <th>VENTE CASH OUT</th>
                        <th className="text-center">TOTAL VENDU</th>
                        <th className="text-center">COMMERCIAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPos?.map((pos, index) => (
                        <tr key={index}>
                          <td className="py-1">
                            <img src="../../../assets/images/dashboard/img_3.jpg" alt="" className="rounded-circle me-2" style={{width:28,height:28}} />
                            {pos.NomPDV}
                          </td>
                          <td>{pos.MSISDN_NSK}</td>
                          <td className="text-center">{pos.nbreCashIN}</td>
                          <td className="text-center">{pos.nbreCashOut}</td>
                          <td className="text-center">{separateNumber(pos.CashIN)}</td>
                          <td className="text-center">{separateNumber(pos.CashOut)}</td>
                          <td className="text-center">
                            <b className="text-danger">{separateNumber(pos.total)}</b>
                          </td>
                          <td className={pos.NOM_COMMERCIAL === "AUCUN COMMERCIAL AFFECTE" ? "text-center" : "text-center font-weight-bold"}>
                            {pos.NOM_COMMERCIAL === "AUCUN COMMERCIAL AFFECTE" ? "" : pos.NOM_COMMERCIAL}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon="mdi-trophy-outline" title="Aucun vendeur sur cette période" />
              )}
            </SectionCard>
          </div>
        </div>

        {/* Evolution annuelle CICO */}
        <div className="row">
          <div className="col-xl-12 stretch-card grid-margin">
            <SectionCard icon="mdi-chart-areaspline" color="primary" title={`Evolution des ventes cash in et cash out de l'année ${moment().format("Y")}`}>
              <div style={{ backgroundColor: "#1e1e2f", padding: "30px", borderRadius: "10px" }}>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={venteSerie} margin={{ top: 10, right: 50, left: 50, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorCashin" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF9933" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#FF9933" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorCashout" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00C49F" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#00C49F" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3c3c3c" />
                    <XAxis dataKey="month" tick={{ fill: "#ccc" }} />
                    <YAxis tick={{ fill: "#ccc" }} />
                    <Tooltip contentStyle={{ backgroundColor: "#333", border: "none", color: "#fff" }} />
                    <Legend />
                    <Area type="monotone" dataKey="cashin" stroke="#FF9933" fill="url(#colorCashin)" strokeWidth={2} />
                    <Area type="monotone" dataKey="cashout" stroke="#00C49F" fill="url(#colorCashout)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </SectionCard>
          </div>
        </div>

        {/* Modal Import */}
        <div className="modal fade" id="exampleModalCenteContrat" tabIndex="-1" role="dialog" aria-hidden="true">
          <div className="modal-dialog modal-md" role="document">
            <form className="modal-content" onSubmit={submitImport}>
              <div className="modal-header">
                <h5 className="modal-title">Importer un template excel</h5>
                <button type="button" className="btn-close text-danger" onClick={() => window.$("#exampleModalCenteContrat").modal("hide")}>
                  <span aria-hidden="true"></span>
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Capture date</label>
                    <input type="date" className="form-control" id="datecapture" onChange={(e) => setDateCapture(e.target.value)} />
                  </div>
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Uploader le fichier</label>
                    <input type="file" className="form-control" required onChange={handleFile} id="file_excel" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => window.$("#exampleModalCenteContrat").modal("hide")}>
                  Fermer
                </button>
                <button type="submit" className={loadingImport ? "btn btn-primary disabled" : "btn btn-primary"}>
                  {loadingImport ? "Enregistrement en cours ..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Modal Filtre */}
        <div className="modal fade" id="exampleModalFiltre" tabIndex="-1" role="dialog" aria-hidden="true">
          <div className="modal-dialog modal-md" role="document">
            <form className="modal-content" onSubmit={filtre_periode_data}>
              <div className="modal-header">
                <h5 className="modal-title">Recherche periodique</h5>
                <button type="button" className="btn-close" onClick={() => window.$("#exampleModalFiltre").modal("hide")}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Date debut</label>
                    <input type="date" className="form-control" onChange={(e) => setDateDebut(e.target.value)} />
                  </div>
                  <div className="mb-3 col-md-12">
                    <label className="form-label">Date Fin</label>
                    <input type="date" className="form-control" onChange={(e) => setDateFin(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                {!loadingStats ? (
                  <button type="submit" className="btn btn-primary">
                    Lancer la recherche
                  </button>
                ) : (
                  <button type="button" className="btn btn-primary disabled form-control">
                    Chargement en cours&nbsp;
                    <div className="spinner-border p-2" role="status"></div>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </ContentPage>
    </>
  );
}

export default MomoHomePage;
